import { Capacitor } from '@capacitor/core'
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  linkWithPopup,
  linkWithCredential,
  signOut,
  type AuthCredential,
  type AuthProvider,
  type UserCredential,
} from 'firebase/auth'
import type { FirebaseError } from 'firebase/app'
import { FirebaseAuthentication } from '@capacitor-firebase/authentication'
import { auth } from './firebase'
import { log } from './logger'

const googleProvider = new GoogleAuthProvider()
const appleProvider = new OAuthProvider('apple.com')
appleProvider.addScope('email')
appleProvider.addScope('name')

// Linking keeps the anonymous session's UID, so everything the guest did — a
// followed totem, a group they're a guest of — carries over with no migration.
// It only works for a brand-new account: an existing one throws and we fall back
// to a plain sign-in, which mints a new UID and leaves the caller to re-apply
// intent from `pendingIntent`.
const LINK_CONFLICT_CODES = new Set([
  'auth/credential-already-in-use',
  'auth/email-already-in-use',
  'auth/account-exists-with-different-credential',
  'auth/provider-already-linked',
])

function isLinkConflict(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code
  return !!code && LINK_CONFLICT_CODES.has(code)
}

async function popupSignInOrLink(
  provider: AuthProvider,
  credentialFromError: (err: unknown) => AuthCredential | null,
): Promise<UserCredential> {
  const current = auth.currentUser
  if (!current?.isAnonymous) return signInWithPopup(auth, provider)

  try {
    const result = await linkWithPopup(current, provider)
    log('auth', 'linked anonymous session to provider')
    return result
  } catch (err) {
    if (!isLinkConflict(err)) throw err
    // The failed link hands back the credential the user just approved, so the
    // fallback needs no second popup — which a browser would block anyway, the
    // click gesture being long spent by now.
    const credential = credentialFromError(err)
    if (!credential) throw err
    log('auth', 'link conflicted with an existing account — signing in instead')
    return signInWithCredential(auth, credential)
  }
}

async function credentialSignInOrLink(credential: AuthCredential): Promise<UserCredential> {
  const current = auth.currentUser
  if (current?.isAnonymous) {
    try {
      const result = await linkWithCredential(current, credential)
      log('auth', 'linked anonymous session to provider')
      return result
    } catch (err) {
      if (!isLinkConflict(err)) throw err
      log('auth', 'link conflicted with an existing account — signing in instead')
    }
  }
  return signInWithCredential(auth, credential)
}

export async function signInWithGoogle(): Promise<UserCredential> {
  if (Capacitor.isNativePlatform()) {
    // skipNativeAuth: the plugin handles only the native Google Sign-In UI and
    // returns an idToken; we then exchange it for a Firebase JS Auth session so
    // the rest of the app (onAuthStateChanged, auth.currentUser) works as on web.
    const result = await FirebaseAuthentication.signInWithGoogle({ skipNativeAuth: true })
    const idToken = result.credential?.idToken
    if (!idToken) throw new Error('Google sign-in returned no idToken')
    return credentialSignInOrLink(GoogleAuthProvider.credential(idToken))
  }
  return popupSignInOrLink(googleProvider, err =>
    GoogleAuthProvider.credentialFromError(err as FirebaseError),
  )
}

export async function signInWithApple(): Promise<UserCredential> {
  if (Capacitor.isNativePlatform()) {
    const result = await FirebaseAuthentication.signInWithApple({ skipNativeAuth: true })
    const idToken = result.credential?.idToken
    const nonce = result.credential?.nonce
    if (!idToken) throw new Error('Apple sign-in returned no idToken')
    return credentialSignInOrLink(appleProvider.credential({ idToken, rawNonce: nonce }))
  }
  return popupSignInOrLink(appleProvider, err =>
    OAuthProvider.credentialFromError(err as FirebaseError),
  )
}

export async function signOutFromAll(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    // Clears the native Google Sign-In token in addition to the JS Auth session,
    // so the next sign-in prompts the account picker instead of silently reusing
    // the previous account.
    await FirebaseAuthentication.signOut()
  }
  await signOut(auth)
}
